import type { LiveReactionEmojiType } from "@/types/graphql/site.generated";
import type { LocalReaction } from "./batch";

export interface BurstLike {
  emojis: { type: LiveReactionEmojiType; count: number }[];
  phrases: { index: number; count: number }[];
}

/** Свои отправленные реакции, которые ещё не пришли в пачке с сервера. */
export interface OwnPending {
  emojis: Partial<Record<LiveReactionEmojiType, number>>;
  phrases: Record<number, number>;
}

/**
 * Своё сверх этого срока уже не ждём: сервер мог отбросить излишек по
 * лимиту, и тогда вычитание съедало бы чужие реакции.
 *
 * Срок покрывает весь путь реакции обратно к зрителю: 1,5 с сбора пачки,
 * запрос к API, дозревание секунды бакета в Redis, тик флаша на воркере
 * (замок 900 мс может пропустить тик) и доставку по WS. На проде это 2,5-4 с,
 * при прежних 3 с своя реакция регулярно прилетала второй раз.
 */
export const PENDING_TTL_MS = 8000;

export const emptyPending = (): OwnPending => ({ emojis: {}, phrases: {} });

export function addPending(
  pending: OwnPending,
  reaction: LocalReaction,
): OwnPending {
  if (reaction.kind === "emoji") {
    return {
      ...pending,
      emojis: {
        ...pending.emojis,
        [reaction.type]: (pending.emojis[reaction.type] ?? 0) + 1,
      },
    };
  }
  return {
    ...pending,
    phrases: {
      ...pending.phrases,
      [reaction.index]: (pending.phrases[reaction.index] ?? 0) + 1,
    },
  };
}

/**
 * Свои реакции зритель видит сразу (оптимистично), поэтому из ближайшей
 * пачки они вычитаются, чтобы не рисовать их дважды.
 */
export function subtractOwn(
  burst: BurstLike,
  pending: OwnPending,
): { burst: BurstLike; pending: OwnPending } {
  const left: OwnPending = {
    emojis: { ...pending.emojis },
    phrases: { ...pending.phrases },
  };

  const emojis = burst.emojis
    .map((emoji) => {
      const own = left.emojis[emoji.type] ?? 0;
      const used = Math.min(own, emoji.count);
      left.emojis[emoji.type] = own - used;
      return { type: emoji.type, count: emoji.count - used };
    })
    .filter((emoji) => emoji.count > 0);

  const phrases = burst.phrases
    .map((phrase) => {
      const own = left.phrases[phrase.index] ?? 0;
      const used = Math.min(own, phrase.count);
      left.phrases[phrase.index] = own - used;
      return { index: phrase.index, count: phrase.count - used };
    })
    .filter((phrase) => phrase.count > 0);

  return { burst: { emojis, phrases }, pending: left };
}

export function burstTotal(burst: BurstLike): number {
  return (
    burst.emojis.reduce((sum, emoji) => sum + emoji.count, 0) +
    burst.phrases.reduce((sum, phrase) => sum + phrase.count, 0)
  );
}
